import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getTeacherHealthList, TeacherHealthSummary } from '../api/supply-health';
import { Empty, Kpi, KpiRow, Note, OpsPage, Pill, Row, Table } from '../../../design-system/ops';

/**
 * Trust Chain — every teacher's operational standing, one row each (UC-A09,
 * ADM-09-FR-01).
 *
 * Replaces the DesignedNotBuilt placeholder with real records and nothing
 * else. Verification and application state are printed as their owners store
 * them: T03 decides eligibility, and a console that summarised it into its own
 * "apto / no apto" would be a second answer to a question it does not own
 * (BM-A09-01). The rating is shown against the governed floor and the version
 * of that floor, so the "bajo el mínimo" mark can be checked against the
 * registry rather than taken on trust.
 */

/** The rating cell: average and count, or the plain fact that nobody has rated yet. */
export const RatingLine: React.FC<{ rating: TeacherHealthSummary['rating'] }> = ({ rating }) =>
  rating.totalReviews === 0 ? (
    <span className="mut">Sin reseñas</span>
  ) : (
    <span>
      <b>{rating.averageRating.toFixed(2)} ★</b>{' '}
      <span className="mut">
        ({rating.totalReviews} reseña{rating.totalReviews === 1 ? '' : 's'})
      </span>{' '}
      {rating.belowFloor ? <Pill tone="bad">Bajo el mínimo</Pill> : null}
    </span>
  );

const AdminTrustChain: React.FC = () => {
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState<TeacherHealthSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getTeacherHealthList();
        if (!cancelled) setTeachers(res.teachers);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'No se pudo cargar la cadena de confianza');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <OpsPage title="Trust Chain">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!teachers) {
    return (
      <OpsPage title="Trust Chain">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const below = teachers.filter((t) => t.rating.belowFloor).length;
  const floor = teachers[0]?.rating;

  return (
    <OpsPage
      title="Trust Chain"
      subtitle="Verificación, oferta, compromisos vendidos y reputación de cada profesor, leídos de su fuente."
    >
      <KpiRow>
        <Kpi label="Profesores" value={teachers.length} />
        <Kpi
          label="Bajo la calificación mínima"
          value={below}
          tone={below > 0 ? 'bad' : 'neutral'}
          note={floor ? `Mínimo ${floor.floor} ★ · ${floor.floorVersion}` : undefined}
        />
      </KpiRow>

      {teachers.length === 0 ? (
        <Empty icon="🏫" title="Sin profesores registrados" />
      ) : (
        <Table
          columns="1.4fr 110px 130px 90px 90px 1.2fr"
          head={['Profesor', 'Verificación', 'Solicitud', 'Cursos', 'Activas', 'Calificación']}
        >
          {teachers.map((t) => (
            <Row
              key={t.teacherId}
              onOpen={() => navigate(`/admin/chain/${encodeURIComponent(t.teacherId)}`)}
              label={`Abrir el perfil operativo de ${t.name}`}
            >
              <span>
                <b>{t.name}</b>
              </span>
              {/* Raw codes, as stored. Eligibility is T03's to decide. */}
              <span>
                {t.verificationStatus ? (
                  <Pill>{t.verificationStatus}</Pill>
                ) : (
                  <span className="mut">—</span>
                )}
              </span>
              <span>
                {t.applicationState ? (
                  <Pill>{t.applicationState}</Pill>
                ) : (
                  <span className="mut">Sin solicitud</span>
                )}
              </span>
              <span>{t.publishedCourses}</span>
              <span>{t.activeEnrollments}</span>
              <RatingLine rating={t.rating} />
            </Row>
          ))}
        </Table>
      )}
    </OpsPage>
  );
};

export default AdminTrustChain;
